import type { LDContext } from 'launchdarkly-react-client-sdk'

type LDContextEntry = { key: string; [attribute: string]: unknown }

const isInternalEmail = (email?: unknown): boolean =>
    typeof email === 'string' && email.endsWith('@algolia.com')

/**
 * Build a LaunchDarkly multi-context for the current session, keyed by store
 * (BigCommerce platform) so the redesign can roll out per store, plus a user
 * context carrying `isInternal` for targeting Algolia staff during QA.
 *
 * @param platform - The current platform/store from AuthContext (shape is `any`).
 * @param user - The current Algolia user from AuthContext (shape is `any`).
 * @returns A multi-context, or null when neither store nor user is known yet.
 */
export function buildLDContext(platform: any, user: any): LDContext | null {
    const storeKey = platform?.platform_id ?? platform?.id
    const userKey = user?.objectID ?? user?.id ?? user?.email

    const contexts: Record<string, LDContextEntry> = {}

    if (storeKey) {
        contexts.store = {
            key: String(storeKey),
            ...(platform?.integration_id
                ? { integrationId: String(platform.integration_id) }
                : {}),
        }
    }

    if (userKey) {
        contexts.user = {
            key: String(userKey),
            ...(user?.email ? { email: String(user.email) } : {}),
            isInternal: isInternalEmail(user?.email),
        }
    }

    if (Object.keys(contexts).length === 0) {
        return null
    }

    const multiContext: LDContext = { kind: 'multi', ...contexts }
    return multiContext
}
